<template>
   <div class="form-wrapper">
      <b-form-row>
         <b-col cols="12" lg="4">
            <b-form-group :invalid-feedback="nameInvalidFeedback">
               <label for="input-name">Contract Researcher Name </label>
               <b-form-input
                  id="input-name"
                  ref="input-name"
                  class="form-input"
                  :state="
                     !$v.contractor.name.$dirty || waiting ? null : !$v.contractor.name.$invalid
                  "
                  :value="contractor.name"
                  @input="
                     (value) =>
                        updateContractor({
                           contractorId,
                           field: 'name',
                           value,
                        })
                  "
                  @blur="$v.contractor.name.$touch()"
                  :disabled="disableInputs"
               ></b-form-input>
            </b-form-group>
         </b-col>

         <b-col cols="12" lg="4">
            <b-form-group>
               <label for="select-type">
                  Fee Type
                  <b-icon id="icon-tooltip-contract-type" :icon="icon.contractType"></b-icon>
                  <b-popover
                     target="icon-tooltip-contract-type"
                     triggers="hover focus"
                     placement="right"
                     @show="popoverActive.contractType = true"
                     @hidden="popoverActive.contractType = false"
                  >
                     Was the contractor paid for their time on an hourly or monthly basis (time and
                     material), or did the contractor receive lump sum payments regardless of their
                     hours worked (fixed fee)?
                  </b-popover>
               </label>
               <b-form-select
                  id="select-type"
                  class="form-input"
                  :class="{'input-warning': edit ? !contractor.contractType : false}"
                  :value="contractor.contractType"
                  :state="
                     !$v.contractor.contractType.$dirty
                        ? null
                        : !$v.contractor.contractType.$invalid
                  "
                  :options="typeOptions"
                  @change="
                     (value) =>
                        updateContractor({
                           contractorId,
                           field: 'contractType',
                           value,
                        })
                  "
                  @blur="$v.contractor.contractType.$touch()"
                  :disabled="disableInputs"
               >
                  <template #first>
                     <b-form-select-option :value="null"></b-form-select-option>
                  </template>
               </b-form-select>
            </b-form-group>
         </b-col>

         <b-col cols="12" lg="4">
            <b-form-group>
               <label for="select-location">
                  Location
                  <b-icon id="icon-tooltip-location" :icon="icon.location"></b-icon>
                  <b-popover
                     target="icon-tooltip-location"
                     triggers="hover focus"
                     placement="right"
                     @show="popoverActive.location = true"
                     @hidden="popoverActive.location = false"
                  >
                     In which state did the contractor perform the qualified R&D activity?
                  </b-popover>
               </label>
               <b-form-select
                  id="select-location"
                  class="form-input"
                  :class="{'input-warning': edit ? !contractor.location : false}"
                  :options="stateOptions"
                  :state="!$v.contractor.location.$dirty ? null : !$v.contractor.location.$invalid"
                  :value="contractor.location"
                  @input="
                     (value) =>
                        updateContractor({
                           contractorId,
                           field: 'location',
                           value,
                        })
                  "
                  @blur="$v.contractor.location.$touch()"
                  :disabled="disableInputs"
               >
                  <template #first>
                     <b-form-select-option :value="null"> </b-form-select-option>
                  </template>
               </b-form-select>
            </b-form-group>
         </b-col>
      </b-form-row>

      <b-form-row>
         <b-col cols="12" order="2" order-lg="1">
            <b-form-group class="mb-0">
               <label for="text-description"
                  >Describe the service provided by this contract researcher.</label
               >
               <b-form-textarea
                  id="text-description"
                  style="min-height: 8.75rem; max-height: 8.75rem"
                  class="form-input"
                  :class="{'input-warning': edit ? !contractor.description : false}"
                  rows="4"
                  :state="
                     !$v.contractor.description.$dirty ? null : !$v.contractor.description.$invalid
                  "
                  :value="contractor.description"
                  @input="
                     (value) =>
                        updateContractor({
                           contractorId,
                           field: 'description',
                           value,
                        })
                  "
                  :disabled="disableInputs"
               ></b-form-textarea>
            </b-form-group>
         </b-col>
      </b-form-row>

      <b-form-row>
         <b-col cols="12" lg="6">
            <div class="d-flex align-items-center" style="height: 100%">
               <p class="form-section-title mb-0">Contract Documents</p>
            </div>
         </b-col>
         <b-col cols="12" lg="6" class="contract-right-side">
            <div>
               <b-form-checkbox
                  id="checkbox-no-document"
                  style="font-size: 0.875rem; font-weight: 400; color: black"
                  :checked="contractor.contractOptOut"
                  @change="
                     (value) =>
                        updateContractor({
                           field: 'contractOptOut',
                           value,
                        })
                  "
                  :disabled="hasSavedContractFiles || disableInputs"
               >
                  There is no contract document available for this contract researcher.
               </b-form-checkbox>
            </div>
         </b-col>
      </b-form-row>

      <b-form-row>
         <b-col cols="12" order="1" order-lg="2" v-if="!contractor.contractOptOut">
            <b-form-group class="mb-0">
               <FileUploadComponent
                  multiple
                  acceptFileTypes=".pdf,.docx,.csv,.xlsx,.xls,.txt"
                  @files-selected="onFilesSelected"
                  :canDeleteFiles="false"
                  :currentFiles="pendingUploadFiles"
                  limits="Limit 100MB"
                  :disabled="contractor.contractOptOut || disableInputs"
               />
            </b-form-group>
            <FilesList
               v-if="edit && contractorId"
               :category-name="contractDocsCategoryName"
               :category="contractDocsCategory"
               :contractor-id="contractorId"
               @files-changed="onContractFilesChanged"
            />
         </b-col>
         <b-col cols="12" order="1" order-lg="2" v-if="contractor.contractOptOut">
            <label for="radio-ip-rights" class="d-block">
               IP rights belong to {{ company.companyName }}
               <b-icon id="icon-tooltip-ip-rights" :icon="icon.companyIpRights"></b-icon>
               <b-popover
                  target="icon-tooltip-ip-rights"
                  triggers="hover focus"
                  placement="right"
                  @show="popoverActive.companyIpRights = true"
                  @hidden="popoverActive.companyIpRights = false"
               >
                  Did your company retain the rights of the contractor’s R&D results, such as the
                  ability to reuse any technology, process, and/or other notable breakthroughs for
                  future R&D efforts?
               </b-popover>
            </label>
            <div class="d-flex align-items-center mb-2" style="gap: 2rem">
               <b-form-radio-group
                  id="radio-ip-rights"
                  :class="{'input-warning': edit ? contractor.companyIpRights === null : false}"
                  :checked="contractor.companyIpRights"
                  :options="[
                     {text: 'Yes', value: true},
                     {text: 'No', value: false},
                  ]"
                  @change="
                     (value) =>
                        updateContractor({
                           field: 'companyIpRights',
                           value,
                        })
                  "
                  size="sm"
                  :disabled="disableInputs"
               ></b-form-radio-group>
               <div class="d-flex align-items-center">
                  <p
                     class="clear-ip-rights-text"
                     @click="
                        !disableInputs
                           ? updateContractor({
                                field: 'companyIpRights',
                                value: null,
                             })
                           : null
                     "
                  >
                     Clear
                  </p>
               </div>
            </div>

            <label for="`radio-eco-risk" class="d-block">
               Economic risk taken by {{ company.companyName }}
               <b-icon id="icon-tooltip-economic-risk" :icon="icon.economicRisk"></b-icon>
               <b-popover
                  target="icon-tooltip-economic-risk"
                  triggers="hover focus"
                  placement="right"
                  @show="popoverActive.economicRisk = true"
                  @hidden="popoverActive.economicRisk = false"
               >
                  Did your company bear the economic risk of the work performed by the contractor,
                  regardless of whether it's successful or not?
               </b-popover>
            </label>
            <div class="d-flex align-items-center mb-2" style="gap: 2rem">
               <b-form-radio-group
                  id="radio-eco-risk"
                  :class="{'input-warning': edit ? contractor.economicRisk === null : false}"
                  :checked="contractor.economicRisk"
                  :options="[
                     {text: 'Yes', value: true},
                     {text: 'No', value: false},
                  ]"
                  @change="
                     (value) =>
                        updateContractor({
                           field: 'economicRisk',
                           value,
                        })
                  "
                  size="sm"
                  :disabled="disableInputs"
               ></b-form-radio-group>
               <p
                  class="clear-eco-risks-text"
                  @click="
                     !disableInputs
                        ? updateContractor({
                             field: 'economicRisk',
                             value: null,
                          })
                        : null
                  "
               >
                  Clear
               </p>
            </div>
         </b-col>
      </b-form-row>

      <div class="form-actions mt-3">
         <b-button
            variant="outline-primary"
            class="form-actions-button"
            @click="onBack"
            :disabled="waiting"
            >Back</b-button
         >
         <b-button
            variant="primary"
            class="form-actions-button"
            @click="onSave"
            :disabled="saveDisabled"
            >Save</b-button
         >
      </div>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {ContractTypes} from '@/store/models/contractor';
import {required, requiredIf} from 'vuelidate/lib/validators';
import {MAX_UPLOAD_SIZE} from '@/helpers/constants';
import FileUploadComponent from '@/components/FileUploadComponent.vue';
import FilesList from '../FilesList.vue';
import {EXPENSE_SUPPORTING_DOCS_UPLOAD_CATEGORY_CONFIG} from '@/helpers/baseConfig';

export default {
   components: {FileUploadComponent, FilesList},
   props: {
      contractorId: {
         type: [String, Number],
         default: null,
      },
      initialContractor: {
         type: Object,
         default: null,
      },
      edit: {
         type: Boolean,
         default: false,
      },
   },

   data() {
      return {
         waiting: false,
         contractorDraft: null,
         pendingUploadFiles: [],
         savedContractFilesCount: 0,
         contractFields: [
            {key: 'name', label: 'Contract File Name', sortable: true},
            {key: 'uploadedAt', sortable: true},
            {key: 'uploadedBy', sortable: true},
            {key: 'actions', label: ''},
         ],
         popoverActive: {
            companyIpRights: false,
            economicRisk: false,
            contractType: false,
            location: false,
         },
      };
   },

   computed: {
      ...mapGetters({
         isRndig: 'isRndig',
         isSME: 'isSME',
         usStates: 'usStatesOutsideUS',
         company: 'companies/currentCompany',
         profile: 'profile',
         users: 'users/companyUsers',
         isComplete: 'contractors/isCompleted',
         contractors: 'contractors/manualVendors',
         uploadCategories: 'uploads/uploadCategories',
      }),

      contractDocsCategoryConfig() {
         return EXPENSE_SUPPORTING_DOCS_UPLOAD_CATEGORY_CONFIG.contractors;
      },

      contractDocsCategoryName() {
         return this.contractDocsCategoryConfig.name;
      },

      contractDocsCategory() {
         return this.contractDocsCategoryConfig.category;
      },

      contractor() {
         return this.contractorDraft;
      },

      disableInputs() {
         return this.waiting || this.isComplete;
      },

      saveDisabled() {
         return (
            this.$v.contractor.$invalid ||
            this.waiting ||
            this.disableInputs ||
            this.fileSizeExceedsLimit ||
            (!this.contractor.contractOptOut && !this.hasAnyContractFiles)
         );
      },

      contractUploadCategory() {
         return this.uploadCategories.find(({name}) => name === this.contractDocsCategoryName);
      },

      savedContractFiles() {
         const files = this.contractUploadCategory?.summary || [];
         if (!this.contractorId) return [];

         return files.filter((file) => {
            const fileContractorId =
               file?.validation?.data?.contractorId ?? file?.contractorId ?? null;
            return String(fileContractorId) === String(this.contractorId);
         });
      },

      hasAnyContractFiles() {
         return this.hasSavedContractFiles || this.pendingUploadFiles.length > 0;
      },

      hasSavedContractFiles() {
         return this.savedContractFilesCount > 0;
      },

      icon() {
         const icons = {};
         for (const [k, v] of Object.entries(this.popoverActive)) {
            icons[k] = v ? 'question-circle-fill' : 'question-circle';
         }
         return icons;
      },

      /** Options for the fee type selector */
      typeOptions() {
         return Object.entries(ContractTypes).map(([key, value]) => {
            return {value: key, text: value};
         });
      },

      /** US States formatted for a b-form-select options attribute */
      stateOptions() {
         return Object.entries(this.usStates)
            .sort((a, b) => {
               const [aKey, aName] = a;
               const [bKey, bName] = b;

               if (aKey === 'XX') {
                  return -1;
               } else if (bKey === 'XX') {
                  return 1;
               }

               if (aName < bName) {
                  return 1;
               } else if (aName < bName) {
                  return -1;
               }
               return 0;
            })
            .map((entry) => {
               const [key, value] = entry;
               return {text: value, value: key};
            });
      },

      /** Invalid feedback for the name input */
      nameInvalidFeedback() {
         const v = this.$v.contractor.name;
         if (!v.$dirty) {
            return '';
         }
         if (!v.required) {
            return 'Required';
         } else if (!v.unique) {
            return 'Duplicate name';
         } else if (!v.minLength) {
            return 'Enter at least 3 characters';
         }
         return '';
      },

      /** Total size of selected files for upload */
      uploadSize() {
         if (this.pendingUploadFiles && this.pendingUploadFiles.length > 0) {
            return this.pendingUploadFiles
               .map((file) => file.size)
               .reduce((sum, item) => sum + item);
         }
         return 0;
      },

      /** Do the selected files exceed the max file size for upload */
      fileSizeExceedsLimit() {
         return this.uploadSize >= MAX_UPLOAD_SIZE;
      },
   },

   methods: {
      initContractorDraft(contractor = null) {
         const source = contractor || {};
         this.contractorDraft = {
            id: source.id ?? null,
            name: source.name || null,
            contractType: source.contractType ?? source.paymentType ?? null,
            location: source.location ?? null,
            description: source.description ?? null,
            contractOptOut: source.contractOptOut ?? false,
            companyIpRights: source.companyIpRights ?? null,
            economicRisk: source.economicRisk ?? null,
            uploadFiles: source.uploadFiles || [],
         };
      },

      /** The name of the user with the given id */
      userName(id) {
         if (id in this.users) {
            const user = this.users[id];
            return `${user.firstName} ${user.lastName}`;
         }
         return 'R&D Incentives Group';
      },

      /** Update a field on a contractor */
      updateContractor({field, value}) {
         this.$set(this.contractorDraft, field, value);
         if (field === 'contractOptOut' && value === false) {
            this.updateContractor({field: 'companyIpRights', value: null});
            this.updateContractor({field: 'economicRisk', value: null});
         }
      },

      normalizeContractorName(name) {
         return typeof name === 'string' ? name.trim() : name;
      },

      /** Delete a contract file */
      async deleteFile(fileId) {
         const proceed = await this.$bvModal.msgBoxConfirm(
            'Are you sure you want to delete this file?',
            {
               title: 'Delete this file?',
               centered: true,
            }
         );
         if (proceed) {
            await this.blockingRequest('contractors/deleteContractFile', {
               companyId: this.company.id,
               contractorId: this.contractorId,
               fileId,
               force: true,
            });
         }
      },

      /** Set files from the file input */
      async onFilesSelected(files) {
         const selectedFiles = Array.from(files || []);
         this.pendingUploadFiles = [...this.pendingUploadFiles, ...selectedFiles];
         this.syncUploadedFiles();
      },

      onContractFilesChanged(fileCount) {
         this.savedContractFilesCount = Number(fileCount) || 0;
         this.syncUploadedFiles();
      },

      syncUploadedFiles() {
         if (!this.contractorDraft) {
            return;
         }
         this.$set(this.contractorDraft, 'uploadFiles', [
            ...(this.savedContractFiles || []),
            ...this.pendingUploadFiles,
         ]);
      },

      /** Emit back action to parent */
      onBack() {
         this.$emit('onBack');
         this.$v.$reset();
         this.$emit('updatePage', false);
      },

      /** Emit save action to parent */
      async onSave() {
         this.waiting = true;

         const normalizedName = this.normalizeContractorName(this.contractor.name);

         const payload = {
            companyId: this.company.id,
            name: normalizedName,
            location: this.contractor.location,
            description: this.contractor.description,
            contractType: this.contractor.contractType,
            contractOptOut: this.contractor.contractOptOut,
            companyIpRights: this.contractor.companyIpRights,
            economicRisk: this.contractor.economicRisk,
         };

         let contractorId = this.contractorId;
         if (this.edit) {
            await this.blockingRequest('contractors/updateManualVendor', {
               vendorId: this.contractorId,
               ...payload,
            });
         } else {
            const contractor = await this.blockingRequest(
               'contractors/createManualVendor',
               payload
            );
            contractorId = contractor?.id || null;
         }

         if (contractorId && this.pendingUploadFiles.length > 0) {
            const newFiles = [...this.pendingUploadFiles];
            this.pendingUploadFiles = [];
            await this.blockingRequest('contractors/uploadContractFiles', {
               companyId: this.company.id,
               contractorId,
               files: newFiles,
            });
            await this.$store.dispatch(
               'uploads/loadSpecialCategory',
               {
                  companyId: this.company.id,
                  category: this.contractDocsCategory,
               },
               {root: true}
            );
            await this.$store.dispatch('contractors/loadContractFiles', {
               contractorId,
            });
         }

         this.closeForm();
      },

      closeForm() {
         this.pendingUploadFiles = [];
         this.waiting = false;
         this.onBack();
         this.$emit('updatePage', true);
      },
   },

   created() {
      this.initContractorDraft(this.initialContractor);
      this.savedContractFilesCount = this.savedContractFiles.length;
   },

   watch: {
      savedContractFiles: {
         immediate: true,
         handler() {
            this.savedContractFilesCount = this.savedContractFiles.length;
            this.syncUploadedFiles();
         },
      },
      pendingUploadFiles: {
         deep: true,
         handler() {
            this.syncUploadedFiles();
         },
      },
   },

   validations: {
      contractor: {
         name: {
            required(value) {
               if (typeof value !== 'string') {
                  return required(value);
               }
               return value.trim().length > 0;
            },
            minLength(value) {
               if (typeof value !== 'string') {
                  return false;
               }
               return this.normalizeContractorName(value).length >= 3;
            },
            unique(name, thisContractor) {
               const normalizedName = this.normalizeContractorName(name);
               return !this.contractors
                  .filter((contractor) => {
                     return contractor.id !== thisContractor.id && contractor.name !== null;
                  })
                  .some((contractor) => {
                     return this.normalizeContractorName(contractor.name) === normalizedName;
                  });
            },
         },
         contractType: {
            required,
         },
         location: {
            required,
         },
         description: {
            required,
         },
         economicRisk: {
            required: requiredIf(function () {
               if (this && this.contractor) {
                  return this.contractor.contractOptOut;
               }
               return false;
            }),
         },
         companyIpRights: {
            required: requiredIf(function () {
               if (this && this.contractor) {
                  return this.contractor.contractOptOut;
               }
               return false;
            }),
         },
         uploadFiles: {
            required: requiredIf(function () {
               if (this && this.contractor) {
                  return !this.contractor.contractOptOut;
               }
               return false;
            }),
         },
      },
   },
};
</script>

<style lang="scss" scoped>
.contract-right-side {
   padding-top: 1rem;
   margin-bottom: 1rem;
}

.form-wrapper {
   label {
      font-size: 0.8125rem;
      font-weight: 400;
      color: black;
   }
}

.form-input {
   font-size: 0.75rem;
   font-weight: 400;
}

.form-section-title {
   font-size: 0.875rem;
   font-weight: bold;
   color: black;
}

.form-actions {
   display: flex;
   justify-content: flex-end;
   align-items: center;
   gap: 0.5rem;
}

.clear-ip-rights-text,
.clear-eco-risks-text {
   font-size: 0.75rem;
   color: $red;
   cursor: pointer;
   margin-bottom: 0;
}

.form-actions-button {
   font-size: 0.75rem;
   width: 7.5rem;
}

::v-deep .custom-radio {
   margin-right: 3rem;

   .custom-control-label {
      font-size: 0.8125rem;
   }
}
</style>
